import React from 'react';
import { createPortal } from 'react-dom';
import { formatFileSize, formatDate } from '../utils/formatters';

/**
 * DeleteConfirmModal Component
 * 
 * Confirmation modal for deleting attachments with options to:
 * - Delete a single version
 * - Delete all versions of a file
 */
const DeleteConfirmModal = ({
  isOpen,
  file,
  versionId, // If set, deleting a specific version; otherwise deleting the whole file
  onConfirm,
  onConfirmAll, // For deleting all versions
  onCancel,
}) => {
  if (!isOpen || !file) return null;

  const hasMultipleVersions = file.versions && file.versions.length > 1;
  const isDeletingVersion = !!versionId;
  const version = isDeletingVersion 
    ? file.versions?.find(v => v.id === versionId) 
    : null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel?.();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onCancel?.();
    }
  };

  const modal = (
    <div 
      className="delete-confirm__backdrop" 
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="delete-confirm__container">
        {/* Icon */}
        <div className="delete-confirm__icon-wrapper">
          <div className="delete-confirm__orbit" />
          <div className="delete-confirm__particle" />
          <div className="delete-confirm__particle delete-confirm__particle--secondary" />
          <div className="delete-confirm__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h3 className="delete-confirm__title">
          {isDeletingVersion ? 'Delete Version' : 'Delete Attachment'}
        </h3>

        {/* File info */}
        <div className="delete-confirm__file-info">
          <span className="delete-confirm__filename">{file.filename}</span>
          {isDeletingVersion && version && (
            <span className="delete-confirm__version-info">
              Version from {formatDate(version.uploadedAt)} • {formatFileSize(version.size)}
            </span>
          )}
          {!isDeletingVersion && (
            <span className="delete-confirm__version-info">
              {hasMultipleVersions 
                ? `${file.versions.length} versions • ${formatFileSize(file.versions.reduce((sum, v) => sum + v.size, 0))} total`
                : formatFileSize(file.size)
              }
            </span>
          )}
        </div>

        {/* Message */}
        <p className="delete-confirm__message">
          {isDeletingVersion 
            ? 'This version will be permanently deleted. This action cannot be undone.'
            : hasMultipleVersions
              ? 'Choose whether to delete just the current version or all versions of this file.'
              : 'This file will be permanently deleted. This action cannot be undone.'
          }
        </p>

        {/* Actions */}
        <div className="delete-confirm__actions">
          <button 
            className="delete-confirm__btn delete-confirm__btn--cancel"
            onClick={onCancel}
          >
            Cancel
          </button>
          
          {!isDeletingVersion && hasMultipleVersions && (
            <button 
              className="delete-confirm__btn delete-confirm__btn--all"
              onClick={onConfirmAll}
            >
              Delete All Versions
            </button>
          )}
          
          <button 
            className="delete-confirm__btn delete-confirm__btn--confirm"
            onClick={onConfirm}
          >
            {isDeletingVersion 
              ? 'Delete Version'
              : hasMultipleVersions 
                ? 'Delete Current Only'
                : 'Delete'
            }
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default DeleteConfirmModal;
